import { useTranslation } from "react-i18next";
import { useContentSharing } from "../../context/ContentSharingContext";
import { useUI } from "../../context/UIContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styled from "styled-components";
import { useState, useEffect } from "react";

// YouTube Video Link Component
export const YouTubeVideoLink = ({ url, children }: { url: string; children: React.ReactNode }) => {
    const { t } = useTranslation('content')
    const { showToast } = useUI()
    const { shareVideo, isContentSharer } = useContentSharing()
    const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null)
    const [videoTitle, setVideoTitle] = useState<string>('')

    // Extract video ID from YouTube URL
    useEffect(() => {
        const getVideoId = (youtubeUrl: string) => {
            const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
            const match = youtubeUrl.match(regExp);
            return (match && match[2].length === 11) ? match[2] : null;
        }

        const videoId = getVideoId(url);
        if (videoId) {
            // Set the high quality thumbnail URL
            setThumbnailUrl(`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`);
            
            // You could fetch the video title here if you have a YouTube API key
            // For now, we'll just use a placeholder or the children prop
            setVideoTitle(children?.toString() || 'YouTube Video');
        }
    }, [url, children]);

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault()
        // Use the shareVideo function to share with other participants
        shareVideo(url)
        // Show a toast notification to the user about whether the video is shared or not
        if (!isContentSharer) {
            // If not the content sharer, inform the user that the video is only playing for them
            showToast(t('videoLink.playingLocallyOnly', 'Video is playing only for you. Share content to share videos with others.'), {
                duration: 5000,
                icon: <FontAwesomeIcon icon={['fas', 'info-circle']} style={{ color: '#48A0F8' }} />
            })
        }
    }

    return (
        <div>
            <YouTubeThumbnailContainer onClick={handleClick}>
                <ThumbnailWrapper>
                    {thumbnailUrl ? (
                        <ThumbnailImage src={thumbnailUrl} alt={videoTitle} />
                    ) : (
                        <ThumbnailPlaceholder>
                            <FontAwesomeIcon icon={['fas', 'play-circle']} />
                        </ThumbnailPlaceholder>
                    )}
                    <PlayButton>
                        <FontAwesomeIcon icon={['fas', 'play']} />
                    </PlayButton>
                </ThumbnailWrapper>
                <VideoInfo>
                    <VideoTitle>{videoTitle}</VideoTitle>
                    <VideoSubtitle>
                        {isContentSharer
                            ? t('videoLink.clickToWatchAndShare', 'Click to watch and share video with others')
                            : t('videoLink.clickToWatch', 'Click to watch video')}
                    </VideoSubtitle>
                </VideoInfo>
            </YouTubeThumbnailContainer>
        </div>
    )
}

const YouTubeThumbnailContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin: 1rem 0;
    background-color: #1a1a1a;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid #333;
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }
`

const ThumbnailWrapper = styled.div`
    position: relative;
    width: 100%;
    padding-top: 56.25%; /* 16:9 aspect ratio */
    background-color: #000;
    overflow: hidden;
`

const ThumbnailImage = styled.img`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
`

const ThumbnailPlaceholder = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #111;
    color: #009dff;
    font-size: 3rem;
`

const PlayButton = styled.div`
    position: absolute;
    bottom: 10px;
    right: 10px;
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #ff0000;
    }
`

const VideoInfo = styled.div`
    padding: 12px 16px;
`

const VideoTitle = styled.h4`
    margin: 0 0 6px 0 !important;
    font-size: 1rem;
    font-weight: 600;
    color: white;
    line-height: 1.2;
    padding: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`

const VideoSubtitle = styled.p`
    margin: 0 !important;
    font-size: 0.8rem;
    line-height: 1.1;
    color: #aaaaaa;
    padding: 0;
`

// Keep these for backward compatibility
const VideoLinkContainer = styled.div``
const VideoContainer = styled.div``
const VideoLinkIcon = styled.div``
const VideoLinkContent = styled.div``
const VideoLinkTitle = styled.h4``
const VideoLinkSubtitle = styled.p``
